import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to track when an element becomes visible in the viewport
 * @param {Function} callback - Function to call when element is in view
 * @param {Object} options - IntersectionObserver options
 * @param {boolean} trackOnce - Whether to track only once (default: true)
 * @returns {Object} ref - Ref to attach to the element you want to observe
 */
export const useInViewTracking = (
  callback,
  options = { threshold: 0.5, triggerOnce: true },
  trackOnce = true
) => {
  const ref = useRef(null);
  const hasTracked = useRef(false);
  const callbackRef = useRef(callback);
  const isInViewRef = useRef(false);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // When callback changes (e.g., products load), check if we should fire
  // This handles the case where element is already visible but callback wasn't ready
  useEffect(() => {
    // Reset tracking when callback changes
    hasTracked.current = false;

    // If element is currently in view, fire the new callback
    if (isInViewRef.current && !hasTracked.current) {
      callback();
      hasTracked.current = true;
    }
  }, [callback]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check if IntersectionObserver is supported (browser only)
    if (typeof IntersectionObserver === 'undefined' || typeof window === 'undefined') {
      console.warn('IntersectionObserver not available, skipping view tracking');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Track whether element is currently in view
          isInViewRef.current = entry.isIntersecting;

          if (entry.isIntersecting) {
            // Track only once if trackOnce is true
            if (trackOnce && hasTracked.current) {
              return;
            }

            callbackRef.current();
            hasTracked.current = true;

            // If trackOnce, disconnect after tracking
            if (trackOnce && options.triggerOnce) {
              observer.disconnect();
            }
          }
        });
      },
      {
        threshold: options.threshold || 0.5,
        rootMargin: options.rootMargin || '0px',
      }
    );

    observer.observe(element);

    return () => {
      if (observer && element) {
        observer.unobserve(element);
      }
    };
  }, [options.threshold, options.rootMargin, options.triggerOnce, trackOnce]);

  return ref;
};

